import { Button } from "../../components/ui/button.jsx";
import { cn } from "../../lib/utils.js";
import {
  MessageCircle,
  Mic,
  MicOff,
  Monitor,
  PhoneOff,
  ShieldCheck,
  Users,
  Video,
  VideoOff,
} from "lucide-react";

function ControlButton({ active, icon: Icon, label, onClick, muted }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-12 w-12 items-center justify-center rounded-full border border-white/10 text-white transition hover:border-white/40 hover:bg-white/10",
        active && "bg-white/15 text-[#25d366]"
      )}
      aria-pressed={active}
      aria-label={label}
      title={label}
    >
      <Icon className={cn("h-5 w-5", muted && "text-[#f15a4f]")} />
    </button>
  );
}

export default function CallStage({
  callType = "video",
  hostName = "You",
  waitingText = "Waiting for others to join…",
  previewImage = "https://i.pravatar.cc/320?img=52",
  isMuted,
  isCameraOff,
  isScreenSharing,
  callEnded = false,
  onToggleMute,
  onToggleCamera,
  onToggleScreen,
  onOpenChat,
  onOpenParticipants,
  onEndCall,
}) {
  const callLabel = callType === "video" ? "Video call" : "Voice call";
  return (
    <div className="glass-card relative overflow-hidden rounded-3xl bg-[#050f17] text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(37,211,102,0.28),transparent_45%),radial-gradient(circle_at_bottom,_rgba(18,140,126,0.25),transparent_55%)]" aria-hidden />
      <div className="relative flex min-h-[360px] flex-col">
        <div className="flex items-start justify-between px-6 pt-5 text-xs uppercase tracking-[0.18em] text-white/70">
          <span>{callLabel}</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[11px] font-medium text-white">
            <ShieldCheck className="h-3.5 w-3.5" /> End-to-end encrypted
          </span>
        </div>
        <div className="flex flex-1 items-center justify-center px-6 pb-6">
          <div className={cn(
            "relative w-full max-w-[420px] overflow-hidden rounded-[32px] border border-white/10 bg-black/40 shadow-[0_20px_45px_rgba(0,0,0,0.45)] transition",
            callEnded && "border-[#25d366]/70 shadow-[0_0_0_6px_rgba(37,211,102,0.35)]",
          )}>
            <div className="relative aspect-[16/12] w-full">
              <img src={previewImage} alt={`${hostName} preview`} className={cn("absolute inset-0 h-full w-full object-cover", isCameraOff && "opacity-40 blur-sm")}/>
              {isCameraOff && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="grid h-20 w-20 place-items-center rounded-full bg-[#1f2c34] text-2xl font-semibold text-white/80">
                    {hostName.slice(0, 2).toUpperCase()}
                  </div>
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-6 pb-6 pt-12 text-left">
                <div className="text-lg font-semibold">{hostName}</div>
                <div className="text-sm text-white/70">{callEnded ? "Call ended for everyone" : waitingText}</div>
              </div>
              {callEnded && (
                <div className="absolute inset-x-8 top-6 rounded-full bg-[#25d366]/20 px-4 py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-[#25d366]">
                  Call ended • Ready to reopen
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 bg-black/30 px-6 py-5">
          <div className="flex items-center gap-3">
            <ControlButton
              active={!isMuted}
              icon={isMuted ? MicOff : Mic}
              label={isMuted ? "Unmute microphone" : "Mute microphone"}
              onClick={onToggleMute}
              muted={isMuted}
            />
            <ControlButton
              active={!isCameraOff}
              icon={isCameraOff ? VideoOff : Video}
              label={isCameraOff ? "Turn camera on" : "Turn camera off"}
              onClick={onToggleCamera}
              muted={isCameraOff}
            />
            <ControlButton
              active={Boolean(isScreenSharing)}
              icon={Monitor}
              label={isScreenSharing ? "Stop sharing screen" : "Share screen"}
              onClick={onToggleScreen}
            />
            <ControlButton
              active={false}
              icon={MessageCircle}
              label="Open chat"
              onClick={onOpenChat}
            />
            <ControlButton
              active={false}
              icon={Users}
              label="View participants"
              onClick={onOpenParticipants}
            />
          </div>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onEndCall}
            className="rounded-full px-4"
          >
            <PhoneOff className="h-4 w-4" /> Leave
          </Button>
        </div>
      </div>
    </div>
  );
}
